import {DateTime} from 'luxon';
import React from 'react';
import styled from 'styled-components';

import {TimeComboBox} from '../../../components/common/dateTime/timeComboBox';
import {CalendarWeekdayToShortName} from '../../../helpers/calendarHelpers';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {BusinessHour} from '../../../models/api/businessHoursModel';
import {CalendarWeekDaysEnum, CalendarWeekDaysShortEnum} from '../../../models/api/calendarCommonTypes';

/*
  Props.
*/

interface BusinessHoursDialogWeekdayHoursProps {
  hours: BusinessHour;
  weekday: CalendarWeekDaysEnum;
  timeFormat: TimeFormatsEnum;
  onSelect: (weekday: CalendarWeekDaysShortEnum, businessHours: BusinessHour) => void;
}

/*
  Style.
*/

const Wrap = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const Label = styled.div`
  width: 76px;
  margin-right: 8px;
  text-transform: capitalize;
  color: ${(p) => p.theme.greys.shade90};
`;

const Time = styled.div`
  width: 78px;
  &:last-child {
    margin-left: 10px;
  }
`;

/*
  Component.
*/

export const BusinessHoursDialogWeekdayHours = ({
  weekday,
  hours,
  timeFormat,
  onSelect,
}: BusinessHoursDialogWeekdayHoursProps) => {
  const selectedOpenTime = DateTime.fromISO(hours.opens);
  const selectedCloseTime = DateTime.fromISO(hours.closes);

  const onTimeSelect = (newHours: BusinessHour) => {
    onSelect(CalendarWeekdayToShortName[weekday], newHours);
  };

  const onOpenTimeSelect = (date: DateTime | undefined) => {
    if (!date) {
      return;
    }

    onTimeSelect({opens: date.toFormat('HH:mm'), closes: hours.closes});
  };

  const onCloseTimeSelect = (date: DateTime | undefined) => {
    if (!date) {
      return;
    }

    onTimeSelect({opens: hours.opens, closes: date.toFormat('HH:mm')});
  };

  return (
    <Wrap data-testid={`${weekday}-hours`}>
      <Label>{weekday}</Label>
      <Time>
        <TimeComboBox
          selectedTime={selectedOpenTime}
          timeFormat={timeFormat}
          onTimeSelect={onOpenTimeSelect}
          maxTime={selectedCloseTime}
          key={`open-time-combo-${selectedOpenTime.valueOf()}`}
          noMargin
        />
      </Time>
      <Time>
        <TimeComboBox
          selectedTime={selectedCloseTime}
          timeFormat={timeFormat}
          onTimeSelect={onCloseTimeSelect}
          minTime={selectedOpenTime}
          key={`close-time-combo-${selectedCloseTime.valueOf()}`}
          noMargin
        />
      </Time>
    </Wrap>
  );
};
